import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Phone, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export const OtpLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const redirect = searchParams.get("redirect") || "/client";

  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [debugInfo, setDebugInfo] = useState(null);
  const inputRefs = useRef([]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const requestOtp = async () => {
    if (!phone || phone.length < 10) {
      toast.error("Entrez un numero de telephone valide");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/otp/request`, { phone });
      setDebugInfo(res.data);
      setStep("code");
      setCooldown(60);
      toast.success(res.data.otp_channel_used === "whatsapp"
        ? "Code envoye par WhatsApp"
        : "Code envoye par SMS");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi du code");
    }
    setLoading(false);
  };

  const resendOtp = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/otp/resend`, { phone });
      setDebugInfo(res.data);
      setCooldown(60);
      toast.success("Code renvoye !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de renvoyer le code");
    }
    setLoading(false);
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, "").split("").slice(0, 6);
      const newCode = [...code];
      digits.forEach((d, i) => { if (index + i < 6) newCode[index + i] = d; });
      setCode(newCode);
      const nextIdx = Math.min(index + digits.length, 5);
      inputRefs.current[nextIdx]?.focus();
      if (newCode.every((c) => c !== "")) verifyCode(newCode.join(""));
      return;
    }
    const newCode = [...code];
    newCode[index] = value.replace(/\D/g, "");
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (newCode.every((c) => c !== "")) verifyCode(newCode.join(""));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const verifyCode = async (fullCode) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/otp/verify`, { phone, code: fullCode });
      const { access_token, user } = res.data;
      loginWithToken(access_token, user);
      toast.success("Connexion reussie !");
      navigate(redirect);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code incorrect");
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col">
      <header className="px-4 py-4 border-b border-zinc-800">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <button onClick={() => step === "code" ? setStep("phone") : navigate(-1)} className="p-2 rounded-lg hover:bg-zinc-800" data-testid="otp-back-btn">
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <h1 className="text-lg font-bold text-white font-['Outfit']">Connexion</h1>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {step === "phone" ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-500/15 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Votre numero de telephone</h2>
                <p className="text-zinc-400 text-sm">Vous recevrez un code de verification par WhatsApp ou SMS</p>
              </div>

              <div>
                <Input
                  type="tel"
                  placeholder="06 12 34 56 78"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white text-center text-lg py-6 rounded-xl"
                  data-testid="otp-phone-input"
                  autoFocus
                />
              </div>

              <Button
                onClick={requestOtp}
                disabled={loading || phone.length < 10}
                className="w-full py-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-base font-medium"
                data-testid="otp-send-btn"
              >
                {loading ? "Envoi..." : "Recevoir le code"}
              </Button>

              {/* Separator */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-zinc-700" />
                <span className="text-zinc-500 text-xs uppercase">ou</span>
                <div className="flex-1 h-px bg-zinc-700" />
              </div>

              {/* Google Sign-In */}
              {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <button
                type="button"
                onClick={() => {
                  const redirectUrl = window.location.origin + '/client';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full flex items-center justify-center gap-3 py-4 px-4 rounded-xl border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 transition-colors"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-white font-medium text-sm">Continuer avec Google</span>
              </button>

              {/* Email/Password link */}
              <p className="text-center text-zinc-500 text-xs">
                <button type="button" onClick={() => navigate('/auth')} className="text-emerald-400 hover:underline" data-testid="email-login-link">
                  Connexion par email et mot de passe
                </button>
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-500/15 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Entrez le code</h2>
                <p className="text-zinc-400 text-sm">
                  Code envoye {debugInfo?.otp_channel_used === "whatsapp" ? "par WhatsApp" : "par SMS"} au <span className="text-white">{phone}</span>
                </p>
              </div>

              <div className="flex justify-center gap-2 sm:gap-3" data-testid="otp-code-inputs">
                {code.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    className="w-11 h-14 sm:w-12 sm:h-14 bg-zinc-900 border border-zinc-700 rounded-xl text-white text-center text-xl font-bold focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-colors"
                    data-testid={`otp-digit-${i}`}
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <div className="text-center">
                <button
                  onClick={resendOtp}
                  disabled={cooldown > 0 || loading}
                  className={`text-sm ${cooldown > 0 ? "text-zinc-600" : "text-emerald-500 hover:text-emerald-400"} transition-colors`}
                  data-testid="otp-resend-btn"
                >
                  {cooldown > 0 ? `Renvoyer dans ${cooldown}s` : "Renvoyer le code"}
                </button>
              </div>

              {loading && (
                <div className="flex justify-center">
                  <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
